import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { hrms, it, getFileUrl } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { theme } from '../../theme';

const tabs = ['Profile', 'Qualification', 'Documents', 'Projects', 'Performance', 'Payslips', 'Leave Balances', 'IT Assets'];

const RELATIONSHIP_OPTIONS = [
  'Father', 'Mother', 'Spouse', 'Son', 'Daughter', 'Brother', 'Sister',
  'Grandfather', 'Grandmother', 'Father-in-Law', 'Mother-in-Law',
  'Brother-in-Law', 'Sister-in-Law', 'Uncle', 'Aunt', 'Other',
];

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', padding: '0.625rem 0', borderBottom: `1px solid ${theme.border}` }}>
      <span style={{ width: '180px', fontSize: '0.8rem', color: theme.textMuted, fontWeight: 500, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: '0.875rem', color: theme.text, fontWeight: 500 }}>{value || '—'}</span>
    </div>
  );
}

function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isHR = ['hr_admin', 'hr_head', 'hr_executive', 'super_admin', 'cfo'].includes(currentUser?.role);
  const [employee, setEmployee] = useState(null);
  const [history, setHistory] = useState({ education: [], skills: [], certifications: [], experience: [] });
  const [projects, setProjects] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [activeTab, setActiveTab] = useState('Profile');
  const [loading, setLoading] = useState(true);
  const [showEduModal, setShowEduModal] = useState(false);
  const [showSkillModal, setShowSkillModal] = useState(false);
  const [showCertModal, setShowCertModal] = useState(false);
  const [eduForm, setEduForm] = useState({ degree: '', institution: '', year: '', field_of_study: '' });
  const [skillForm, setSkillForm] = useState({ name: '', level: 'intermediate', years_of_experience: '' });
  const [certForm, setCertForm] = useState({ name: '', issuer: '', issue_date: '', expiry_date: '' });
  const [certFile, setCertFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pfEsiEdit, setPfEsiEdit] = useState(false);
  const [pfEsiForm, setPfEsiForm] = useState({ pf_uan: '', esic_number: '', esi_hospital: '' });
  const [pfEsiSaving, setPfEsiSaving] = useState(false);

  // Batch 3 state
  const [bankAccounts, setBankAccounts] = useState([]);
  const [nominees, setNominees]         = useState([]);
  const [empDocs, setEmpDocs]           = useState([]);
  const [showBankModal, setShowBankModal] = useState(false);
  const [showNomineeModal, setShowNomineeModal] = useState(false);
  const [bankForm, setBankForm] = useState({ bank_name: '', bank_branch: '', ifsc_code: '', account_number: '', account_type: 'savings', account_holder: '', is_primary: false });
  const [nomineeForm, setNomineeForm] = useState({ name: '', relation: '', dob: '', aadhar: '', contact: '', percentage: '' });
  const [editBankId, setEditBankId]   = useState(null);
  const [editNomineeId, setEditNomineeId] = useState(null);
  const [empAssets, setEmpAssets] = useState({ assets: [], licenses: [] });

  useEffect(() => {
    loadEmployee();
    loadBankAccounts();
    loadNominees();
    loadDocs();
    loadEmpAssets();
  }, [id]);

  const loadBankAccounts = () =>
    hrms.getBankAccounts(id).then(r => setBankAccounts(r.data || [])).catch(() => {});
  const loadNominees = () =>
    hrms.getNominees(id).then(r => setNominees(r.data || [])).catch(() => {});
  const loadDocs = () =>
    hrms.getEmpDocuments(id).then(r => setEmpDocs(r.data || [])).catch(() => {});
  const loadEmpAssets = () =>
    it.getEmployeeAssets(id).then(r => setEmpAssets(r.data || { assets: [], licenses: [] })).catch(() => {});

  const loadEmployee = async () => {
    setLoading(true);
    try {
      const [empRes, histRes] = await Promise.allSettled([
        hrms.getEmployee(id),
        hrms.getEmployeeHistory(id),
      ]);
      if (empRes.status === 'fulfilled') {
        setEmployee(empRes.value.data);
        const e = empRes.value.data;
        setPfEsiForm({ pf_uan: e.pf_uan || '', esic_number: e.esic_number || '', esi_hospital: e.esi_hospital || '' });
      }
      if (histRes.status === 'fulfilled') {
        const h = histRes.value.data;
        setHistory({
          education: h.education || [],
          skills: h.skills || [],
          certifications: h.certifications || [],
          experience: h.experience || [],
        });
      }
      // Load additional data
      const [projRes, lbRes, prRes] = await Promise.allSettled([
        hrms.getEmployeeProjects(id),
        hrms.getLeaveBalances({ employee_id: id }),
        hrms.getPayrollRecords({ employee_id: id }),
      ]);
      if (projRes.status === 'fulfilled') {
        setProjects(projRes.value.data || []);
      }
      if (lbRes.status === 'fulfilled') setLeaveBalances(lbRes.value.data || []);
      if (prRes.status === 'fulfilled') {
        const d = prRes.value.data;
        setPayrollRecords(Array.isArray(d) ? d : d.items || []);
      }
    } catch (err) {
      console.warn('[EmployeeDetail] Failed to load data:', err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEducation = async () => {
    setSaving(true);
    try {
      await hrms.addEducation(id, eduForm);
      setShowEduModal(false);
      setEduForm({ degree: '', institution: '', year: '', field_of_study: '' });
      loadEmployee();
    } catch { alert('Failed to add education.'); }
    finally { setSaving(false); }
  };

  const handleAddSkill = async () => {
    setSaving(true);
    try {
      await hrms.addSkill(id, skillForm);
      setShowSkillModal(false);
      setSkillForm({ name: '', level: 'intermediate', years_of_experience: '' });
      loadEmployee();
    } catch { alert('Failed to add skill.'); }
    finally { setSaving(false); }
  };

  const handleAddCert = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(certForm).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (certFile) fd.append('file', certFile);
      await hrms.addCertification(id, fd);
      setShowCertModal(false);
      setCertForm({ name: '', issuer: '', issue_date: '', expiry_date: '' });
      setCertFile(null);
      loadEmployee();
    } catch { alert('Failed to add certification.'); }
    finally { setSaving(false); }
  };

  const inp = { width: '100%', padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', marginBottom: '0.75rem', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.35rem' };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px' }}>
        <div style={{ width: '40px', height: '40px', border: `3px solid ${theme.border}`, borderTop: `3px solid ${theme.primary}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  if (!employee) return <p style={{ color: theme.textMuted }}>Employee not found.</p>;

  return (
    <div style={{ maxWidth: '1000px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: theme.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', border: `3px solid ${theme.border}`, fontSize: '1.8rem', color: theme.primaryDark, flexShrink: 0 }}>
            {employee.profile_photo ? <img src={getFileUrl(employee.profile_photo)} alt={employee.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : employee.full_name?.charAt(0)?.toUpperCase()}
          </div>
          <div>
            <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: theme.text, margin: 0 }}>{employee.full_name}</h1>
            <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0' }}>
              {employee.position?.title || employee.position?.name || employee.position_name} · {employee.department?.name || employee.department_name}
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <Badge status={employee.employment_status || employee.status || 'active'} />
              <span style={{ fontSize: '0.75rem', color: theme.textMuted, background: theme.pageBg, padding: '0.1rem 0.5rem', borderRadius: '9999px', border: `1px solid ${theme.border}` }}>
                {employee.emp_code}
              </span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <Button variant="outline" onClick={() => navigate(`/hrms/employees/${id}/edit`)}>Edit</Button>
          <Button variant="outlineGray" onClick={() => navigate('/hrms/employees')}>← Back</Button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: `2px solid ${theme.border}`, marginBottom: '1.5rem', overflowX: 'auto' }}>
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '0.625rem 1.25rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontWeight: activeTab === tab ? 700 : 400,
              color: activeTab === tab ? theme.primary : theme.textMuted,
              borderBottom: activeTab === tab ? `2px solid ${theme.primary}` : '2px solid transparent',
              marginBottom: '-2px',
              fontSize: '0.875rem',
              whiteSpace: 'nowrap',
              transition: 'all 0.15s',
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'Profile' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Row 1: Personal + Employment */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <Card title="Personal Information">
              <InfoRow label="Display Name" value={employee.full_name} />
              <InfoRow label="Name as per Aadhaar" value={employee.name_as_per_aadhar} />
              <InfoRow label="Father / Husband Name" value={employee.father_husband_name} />
              <InfoRow label="Date of Birth" value={employee.dob ? new Date(employee.dob).toLocaleDateString('en-IN') : null} />
              <InfoRow label="Gender" value={employee.gender} />
              <InfoRow label="Blood Group" value={employee.blood_group} />
              <InfoRow label="Marital Status" value={employee.marital_status} />
              <InfoRow label="Qualification" value={employee.qualification} />
              <InfoRow label="Personal Email" value={employee.personal_email} />
              <InfoRow label="Phone" value={employee.phone} />
              <InfoRow label="Alternate Phone" value={employee.alternate_phone} />
              <InfoRow label="Present Address" value={employee.present_address} />
              <InfoRow label="Permanent Address" value={employee.address} />
              <InfoRow label="Emergency Contact" value={employee.emergency_contact} />
            </Card>
            <Card title="Employment Details">
              <InfoRow label="Employee Code" value={employee.emp_code} />
              <InfoRow label="Work Email" value={employee.email} />
              <InfoRow label="Hire Date" value={employee.hire_date ? new Date(employee.hire_date).toLocaleDateString('en-IN') : null} />
              <InfoRow label="Employment Type" value={employee.employment_type?.replace(/_/g, ' ')} />
              <InfoRow label="Status" value={employee.employment_status?.replace(/_/g, ' ')} />
              <InfoRow label="Department" value={employee.department?.name || employee.department_name} />
              <InfoRow label="Team" value={employee.team?.name || employee.team_name} />
              <InfoRow label="Position" value={employee.position?.title || employee.position?.name || employee.position_name} />
              <InfoRow label="Reporting Manager" value={employee.reporting_manager?.full_name || employee.reporting_manager_name} />
              <InfoRow label="Grade" value={employee.grade?.name} />
              {employee.employment_type === 'contract' && (
                <InfoRow label="Contractor" value={employee.contractor?.name} />
              )}
              <InfoRow label="PF UAN" value={employee.pf_uan} />
              <InfoRow label="ESIC Number" value={employee.esic_number} />
              <InfoRow label="ESI Hospital" value={employee.esi_hospital} />
            </Card>
          </div>

          {/* HR Admin: Edit PF / ESI */}
          {isHR && (
            <Card title="PF & ESI — HR Edit">
              {!pfEsiEdit ? (
                <div>
                  <InfoRow label="PF UAN" value={employee.pf_uan} />
                  <InfoRow label="ESIC Number" value={employee.esic_number} />
                  <InfoRow label="ESI Hospital" value={employee.esi_hospital} />
                  <div style={{ marginTop: '0.75rem' }}>
                    <Button size="sm" variant="outline" onClick={() => setPfEsiEdit(true)}>Edit</Button>
                  </div>
                </div>
              ) : (
                <div>
                  {[['pf_uan', 'PF UAN (UAN No)', 'UAN number'], ['esic_number', 'ESIC Number', 'ESI number'], ['esi_hospital', 'ESI Hospital Location', 'Nearest ESI hospital']].map(([field, label, ph]) => (
                    <div key={field} style={{ marginBottom: '0.75rem' }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.3rem' }}>{label}</label>
                      <input style={{ width: '100%', padding: '0.5rem 0.75rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }}
                        value={pfEsiForm[field]} onChange={e => setPfEsiForm(p => ({ ...p, [field]: e.target.value }))} placeholder={ph} />
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <Button size="sm" variant="primary" loading={pfEsiSaving} onClick={async () => {
                      setPfEsiSaving(true);
                      try {
                        await hrms.updateEmployee(id, pfEsiForm);
                        setEmployee(prev => ({ ...prev, ...pfEsiForm }));
                        setPfEsiEdit(false);
                      } catch { alert('Failed to save PF/ESI details.'); }
                      finally { setPfEsiSaving(false); }
                    }}>Save</Button>
                    <Button size="sm" variant="outlineGray" onClick={() => setPfEsiEdit(false)}>Cancel</Button>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Row 2: Identity Documents + Bank Accounts */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <Card title="Identity Documents">
              <InfoRow label="Aadhar Number" value={employee.aadhar_number} />
              <InfoRow label="PAN Number" value={employee.pan_number} />
            </Card>
            <Card title="Bank Accounts" titleAction={
              <Button size="sm" variant="primary" onClick={() => { setBankForm({ bank_name: '', bank_branch: '', ifsc_code: '', account_number: '', account_type: 'savings', account_holder: '', is_primary: false }); setEditBankId(null); setShowBankModal(true); }}>+ Add</Button>
            }>
              {bankAccounts.length === 0 ? (
                <p style={{ color: theme.textMuted, fontSize: '0.875rem', padding: '0.5rem 0' }}>No bank accounts added</p>
              ) : bankAccounts.map((b) => (
                <div key={b.id} style={{ padding: '0.625rem 0', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: '0.875rem', color: theme.text }}>
                      {b.bank_name} {b.is_primary && <span style={{ background: '#D1FAE5', color: '#065F46', fontSize: '0.7rem', padding: '0.1rem 0.4rem', borderRadius: '999px', marginLeft: '0.4rem' }}>Primary</span>}
                    </p>
                    <p style={{ margin: '0.15rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>A/C: {b.account_number} · IFSC: {b.ifsc_code || '—'}</p>
                    <p style={{ margin: '0.1rem 0 0', fontSize: '0.75rem', color: theme.textMuted }}>{b.bank_branch} · {b.account_type}</p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <Button size="sm" variant="outline" onClick={() => { setBankForm({ ...b }); setEditBankId(b.id); setShowBankModal(true); }}>Edit</Button>
                    <Button size="sm" variant="danger" onClick={async () => { if (window.confirm('Delete?')) { await hrms.deleteBankAccount(id, b.id); loadBankAccounts(); } }}>Del</Button>
                  </div>
                </div>
              ))}
            </Card>
          </div>

          {/* Row 3: Nominees with % */}
          <Card title="Nominees" titleAction={
            <Button size="sm" variant="primary" onClick={() => { setNomineeForm({ name: '', relation: '', dob: '', aadhar: '', contact: '', percentage: '' }); setEditNomineeId(null); setShowNomineeModal(true); }}>+ Add</Button>
          }>
            {nominees.length === 0 ? (
              <p style={{ color: theme.textMuted, fontSize: '0.875rem', padding: '0.5rem 0' }}>No nominees added</p>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.8rem', color: nominees.reduce((s, n) => s + n.percentage, 0) === 100 ? '#10B981' : theme.danger, fontWeight: 600 }}>
                    Total: {nominees.reduce((s, n) => s + n.percentage, 0)}% {nominees.reduce((s, n) => s + n.percentage, 0) === 100 ? '✓' : '(must be 100%)'}
                  </span>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ background: theme.pageBg }}>
                      {['Name', 'Relation', 'DOB', 'Aadhar', 'Contact', '%', ''].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '0.5rem 0.75rem', fontWeight: 600, fontSize: '0.8rem', color: theme.textMuted, borderBottom: `1px solid ${theme.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {nominees.map((n, idx) => (
                      <tr key={n.id ?? `flat-${idx}`} style={{ borderBottom: `1px solid ${theme.border}` }}>
                        <td style={{ padding: '0.5rem 0.75rem', fontWeight: 500 }}>{n.name}</td>
                        <td style={{ padding: '0.5rem 0.75rem', color: theme.textMuted }}>{n.relation || '—'}</td>
                        <td style={{ padding: '0.5rem 0.75rem', color: theme.textMuted }}>{n.dob ? new Date(n.dob).toLocaleDateString('en-IN') : '—'}</td>
                        <td style={{ padding: '0.5rem 0.75rem', color: theme.textMuted }}>{n.aadhar || '—'}</td>
                        <td style={{ padding: '0.5rem 0.75rem', color: theme.textMuted }}>{n.contact || '—'}</td>
                        <td style={{ padding: '0.5rem 0.75rem', fontWeight: 700, color: theme.primary }}>{n.percentage}%</td>
                        <td style={{ padding: '0.5rem 0.75rem' }}>
                          {n.id != null && (
                            <div style={{ display: 'flex', gap: '0.4rem' }}>
                              <Button size="sm" variant="outline" onClick={() => { setNomineeForm({ name: n.name, relation: n.relation || '', dob: n.dob || '', aadhar: n.aadhar || '', contact: n.contact || '', percentage: n.percentage }); setEditNomineeId(n.id); setShowNomineeModal(true); }}>Edit</Button>
                              <Button size="sm" variant="danger" onClick={async () => { if (window.confirm('Delete?')) { await hrms.deleteNominee(id, n.id); loadNominees(); } }}>Del</Button>
                            </div>
                          )}
                          {n.id == null && <span style={{ fontSize: '0.72rem', color: theme.textMuted }}>From profile</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </Card>

          {/* Row 4: Family Members */}
          {employee.family_members && (() => {
            let members = [];
            try { members = JSON.parse(employee.family_members); } catch { members = []; }
            if (!Array.isArray(members) || members.length === 0) return null;
            return (
              <Card title="Family Members">
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ background: theme.pageBg }}>
                      {['Name', 'Relation', 'Date of Birth', 'Aadhar'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '0.5rem 0.75rem', fontWeight: 600, fontSize: '0.8rem', color: theme.textMuted, borderBottom: `1px solid ${theme.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${theme.border}` }}>
                        <td style={{ padding: '0.5rem 0.75rem', color: theme.text, fontWeight: 500 }}>{m.name || '—'}</td>
                        <td style={{ padding: '0.5rem 0.75rem', color: theme.text }}>{m.relation || '—'}</td>
                        <td style={{ padding: '0.5rem 0.75rem', color: theme.text }}>{m.dob ? new Date(m.dob).toLocaleDateString('en-IN') : '—'}</td>
                        <td style={{ padding: '0.5rem 0.75rem', color: theme.text }}>{m.aadhar || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            );
          })()}
        </div>
      )}

      {activeTab === 'Documents' && (
        <DocumentsTab empId={id} docs={empDocs} onRefresh={loadDocs} />
      )}

      {activeTab === 'Qualification' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Education */}
          <Card title="Education" titleAction={<Button size="sm" variant="primary" onClick={() => setShowEduModal(true)}>+ Add</Button>}>
            {history.education.length === 0 ? (
              <p style={{ color: theme.textMuted, textAlign: 'center', padding: '1.5rem 0' }}>No education records</p>
            ) : history.education.map((e, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: `1px solid ${theme.border}` }}>
                <div>
                  <p style={{ fontWeight: 600, margin: 0 }}>{e.degree}</p>
                  <p style={{ fontSize: '0.8rem', color: theme.textMuted, margin: '0.2rem 0 0' }}>{e.institution} · {e.year}</p>
                </div>
                <Button size="sm" variant="danger" onClick={async () => { if (window.confirm('Delete?')) { await hrms.deleteEducation(id, e.id); loadEmployee(); } }}>Delete</Button>
              </div>
            ))}
          </Card>

          {/* Skills */}
          <Card title="Skills" titleAction={<Button size="sm" variant="primary" onClick={() => setShowSkillModal(true)}>+ Add</Button>}>
            {history.skills.length === 0 ? (
              <p style={{ color: theme.textMuted, textAlign: 'center', padding: '1.5rem 0' }}>No skills added</p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.625rem', paddingTop: '0.5rem' }}>
                {history.skills.map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: theme.lightBg, border: `1px solid ${theme.primaryLight}`, borderRadius: '9999px', padding: '0.3rem 0.875rem' }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: 600, color: theme.primaryDark }}>{s.name}</span>
                    <span style={{ fontSize: '0.7rem', color: theme.textMuted }}>· {s.level}</span>
                    <button onClick={async () => { if (window.confirm('Delete?')) { await hrms.deleteSkill(id, s.id); loadEmployee(); } }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.danger, fontSize: '0.75rem', padding: '0 0.1rem' }}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Certifications */}
          <Card title="Certifications" titleAction={<Button size="sm" variant="primary" onClick={() => setShowCertModal(true)}>+ Add</Button>}>
            {history.certifications.length === 0 ? (
              <p style={{ color: theme.textMuted, textAlign: 'center', padding: '1.5rem 0' }}>No certifications</p>
            ) : history.certifications.map((c, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: `1px solid ${theme.border}` }}>
                <div>
                  <p style={{ fontWeight: 600, margin: 0 }}>{c.name}</p>
                  <p style={{ fontSize: '0.8rem', color: theme.textMuted, margin: '0.2rem 0 0' }}>{c.issuer} · {c.issue_date}</p>
                </div>
                <Button size="sm" variant="danger" onClick={async () => { if (window.confirm('Delete?')) { await hrms.deleteCertification(id, c.id); loadEmployee(); } }}>Delete</Button>
              </div>
            ))}
          </Card>

          {/* Past Experience */}
          <Card title="Past Experience">
            {history.experience.length === 0 ? (
              <p style={{ color: theme.textMuted, textAlign: 'center', padding: '1.5rem 0' }}>No experience records</p>
            ) : (
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: 19, top: 24, bottom: 8, width: 2, background: theme.border }} />
                {history.experience.map((exp) => {
                  const fromStr = exp.from_date ? new Date(exp.from_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '';
                  const toStr = exp.to_date ? new Date(exp.to_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : 'Present';
                  let duration = '';
                  if (exp.from_date) {
                    const start = new Date(exp.from_date);
                    const end = exp.to_date ? new Date(exp.to_date) : new Date();
                    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
                    const yrs = Math.floor(months / 12);
                    const mos = months % 12;
                    duration = yrs > 0 ? `${yrs} yr${yrs > 1 ? 's' : ''}${mos > 0 ? ` ${mos} mo` : ''}` : `${mos} mo`;
                  }
                  return (
                    <div key={exp.id} style={{ display: 'flex', gap: '0.875rem', paddingBottom: '1.25rem', position: 'relative' }}>
                      <div style={{ width: 40, height: 40, borderRadius: '0.5rem', background: '#EFF6FF', border: '1px solid #BFDBFE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0, zIndex: 1 }}>🏢</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <p style={{ margin: 0, fontWeight: 700, fontSize: '0.9rem', color: theme.text }}>{exp.designation}</p>
                            <p style={{ margin: '0.1rem 0 0', fontWeight: 600, fontSize: '0.82rem', color: theme.primary }}>{exp.company_name}</p>
                            <p style={{ margin: '0.1rem 0 0', fontSize: '0.77rem', color: theme.textMuted }}>
                              {fromStr}{fromStr ? ' – ' : ''}{toStr}{duration ? ` · ${duration}` : ''}
                            </p>
                          </div>
                          <Button size="sm" variant="danger" onClick={async () => { if (window.confirm('Delete?')) { await hrms.deleteExperience(id, exp.id); loadEmployee(); } }}>Delete</Button>
                        </div>
                        {exp.description && (
                          <p style={{ margin: '0.4rem 0 0', fontSize: '0.8rem', color: theme.gray, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{exp.description}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      )}

      {activeTab === 'Projects' && (
        <Card title="Assigned Projects">
          {projects.length === 0 ? (
            <p style={{ color: theme.textMuted, textAlign: 'center', padding: '2rem 0' }}>No projects assigned</p>
          ) : projects.map((p, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', borderBottom: `1px solid ${theme.border}` }}>
              <div>
                <p style={{ fontWeight: 600, margin: 0 }}>{p.project_name || p.name}</p>
                <p style={{ fontSize: '0.8rem', color: theme.textMuted, margin: '0.2rem 0 0' }}>
                  {p.role_in_project ? `Role: ${p.role_in_project}` : ''}{p.project_status ? ` · ${p.project_status}` : ''}
                </p>
              </div>
              <Badge status={p.project_status || p.status || 'active'} />
            </div>
          ))}
        </Card>
      )}

      {activeTab === 'Leave Balances' && (
        <Card title="Leave Balances">
          {leaveBalances.length === 0 ? (
            <p style={{ color: theme.textMuted, textAlign: 'center', padding: '2rem 0' }}>No leave balances</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
              {leaveBalances.map((lb, i) => (
                <div key={i} style={{ background: theme.lightBg, borderRadius: '0.5rem', padding: '1rem', border: `1px solid ${theme.primaryLight}`, textAlign: 'center' }}>
                  <p style={{ fontSize: '0.8rem', color: theme.textMuted, margin: '0 0 0.5rem', fontWeight: 600 }}>{lb.leave_type_name || lb.leave_type?.name}</p>
                  <p style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.primary, margin: 0 }}>{lb.balance ?? lb.remaining}</p>
                  <p style={{ fontSize: '0.7rem', color: theme.textMuted, margin: '0.25rem 0 0' }}>days remaining</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {activeTab === 'Payslips' && (
        <Card title="Payroll Records">
          {payrollRecords.length === 0 ? (
            <p style={{ color: theme.textMuted, textAlign: 'center', padding: '2rem 0' }}>No payroll records</p>
          ) : payrollRecords.map((r, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', borderBottom: `1px solid ${theme.border}` }}>
              <div>
                <p style={{ fontWeight: 600, margin: 0 }}>{r.period_name || r.period?.name}</p>
                <p style={{ fontSize: '0.8rem', color: theme.textMuted, margin: '0.2rem 0 0' }}>Net: ₹{r.net_pay?.toLocaleString()}</p>
              </div>
              <Badge status={r.status || 'paid'} />
            </div>
          ))}
        </Card>
      )}

      {activeTab === 'Performance' && (
        <Card title="Performance Reviews">
          <p style={{ color: theme.textMuted, textAlign: 'center', padding: '2rem 0' }}>Performance data will be shown here.</p>
        </Card>
      )}

      {activeTab === 'IT Assets' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <Card title={`Assigned Assets (${empAssets.assets.length})`}>
            {empAssets.assets.length === 0 ? (
              <p style={{ color: theme.textMuted, textAlign: 'center', padding: '1.5rem 0' }}>No assets assigned</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: theme.pageBg }}>
                    {['Asset Code', 'Name', 'Category', 'Serial No.', 'Model', 'Assigned Date'].map(h => (
                      <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.72rem', color: theme.textMuted, borderBottom: `1px solid ${theme.border}`, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {empAssets.assets.map((a) => (
                    <tr key={a.assignment_id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                      <td style={{ padding: '0.6rem 0.75rem', fontWeight: 600, color: theme.primary }}>{a.asset_code || '—'}</td>
                      <td style={{ padding: '0.6rem 0.75rem' }}>{a.asset_name || '—'}</td>
                      <td style={{ padding: '0.6rem 0.75rem', color: theme.textMuted }}>{a.category || '—'}</td>
                      <td style={{ padding: '0.6rem 0.75rem', color: theme.textMuted }}>{a.serial_number || '—'}</td>
                      <td style={{ padding: '0.6rem 0.75rem', color: theme.textMuted }}>{a.model || '—'}</td>
                      <td style={{ padding: '0.6rem 0.75rem', color: theme.textMuted }}>{a.assigned_date ? new Date(a.assigned_date).toLocaleDateString('en-IN') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
          <Card title={`Assigned Licenses (${empAssets.licenses.length})`}>
            {empAssets.licenses.length === 0 ? (
              <p style={{ color: theme.textMuted, textAlign: 'center', padding: '1.5rem 0' }}>No licenses assigned</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: theme.pageBg }}>
                    {['Software', 'License Name', 'License Key', 'Assigned Date', 'Expiry'].map(h => (
                      <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.72rem', color: theme.textMuted, borderBottom: `1px solid ${theme.border}`, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {empAssets.licenses.map((l) => (
                    <tr key={l.assignment_id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                      <td style={{ padding: '0.6rem 0.75rem', fontWeight: 600 }}>{l.software || '—'}</td>
                      <td style={{ padding: '0.6rem 0.75rem' }}>{l.license_name || '—'}</td>
                      <td style={{ padding: '0.6rem 0.75rem', color: theme.textMuted, fontFamily: 'monospace', fontSize: '0.78rem' }}>{l.license_key || '—'}</td>
                      <td style={{ padding: '0.6rem 0.75rem', color: theme.textMuted }}>{l.assigned_date ? new Date(l.assigned_date).toLocaleDateString('en-IN') : '—'}</td>
                      <td style={{ padding: '0.6rem 0.75rem', color: l.expiry_date && new Date(l.expiry_date) < new Date() ? theme.danger : theme.textMuted }}>
                        {l.expiry_date ? new Date(l.expiry_date).toLocaleDateString('en-IN') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      )}

      {/* Modals */}
      <Modal isOpen={showEduModal} onClose={() => setShowEduModal(false)} title="Add Education"
        footer={<><Button variant="outlineGray" onClick={() => setShowEduModal(false)}>Cancel</Button><Button variant="primary" loading={saving} onClick={handleAddEducation}>Add</Button></>}>
        <label style={lbl}>Degree *</label>
        <input style={inp} value={eduForm.degree} onChange={(e) => setEduForm({ ...eduForm, degree: e.target.value })} placeholder="B.Tech / MBA..." />
        <label style={lbl}>Institution *</label>
        <input style={inp} value={eduForm.institution} onChange={(e) => setEduForm({ ...eduForm, institution: e.target.value })} placeholder="University name" />
        <label style={lbl}>Field of Study</label>
        <input style={inp} value={eduForm.field_of_study} onChange={(e) => setEduForm({ ...eduForm, field_of_study: e.target.value })} placeholder="Computer Science..." />
        <label style={lbl}>Year of Completion</label>
        <input style={inp} type="number" value={eduForm.year} onChange={(e) => setEduForm({ ...eduForm, year: e.target.value })} placeholder="2022" />
      </Modal>

      <Modal isOpen={showSkillModal} onClose={() => setShowSkillModal(false)} title="Add Skill"
        footer={<><Button variant="outlineGray" onClick={() => setShowSkillModal(false)}>Cancel</Button><Button variant="primary" loading={saving} onClick={handleAddSkill}>Add</Button></>}>
        <label style={lbl}>Skill Name *</label>
        <input style={inp} value={skillForm.name} onChange={(e) => setSkillForm({ ...skillForm, name: e.target.value })} placeholder="React, Python..." />
        <label style={lbl}>Level</label>
        <select style={inp} value={skillForm.level} onChange={(e) => setSkillForm({ ...skillForm, level: e.target.value })}>
          <option value="beginner">Beginner</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
          <option value="expert">Expert</option>
        </select>
        <label style={lbl}>Years of Experience</label>
        <input style={inp} type="number" value={skillForm.years_of_experience} onChange={(e) => setSkillForm({ ...skillForm, years_of_experience: e.target.value })} placeholder="2" />
      </Modal>

      <Modal isOpen={showCertModal} onClose={() => setShowCertModal(false)} title="Add Certification"
        footer={<><Button variant="outlineGray" onClick={() => setShowCertModal(false)}>Cancel</Button><Button variant="primary" loading={saving} onClick={handleAddCert}>Add</Button></>}>
        <label style={lbl}>Certification Name *</label>
        <input style={inp} value={certForm.name} onChange={(e) => setCertForm({ ...certForm, name: e.target.value })} placeholder="AWS Solutions Architect..." />
        <label style={lbl}>Issuer</label>
        <input style={inp} value={certForm.issuer} onChange={(e) => setCertForm({ ...certForm, issuer: e.target.value })} placeholder="Amazon Web Services..." />
        <label style={lbl}>Issue Date</label>
        <input style={inp} type="date" value={certForm.issue_date} onChange={(e) => setCertForm({ ...certForm, issue_date: e.target.value })} />
        <label style={lbl}>Expiry Date</label>
        <input style={inp} type="date" value={certForm.expiry_date} onChange={(e) => setCertForm({ ...certForm, expiry_date: e.target.value })} />
        <label style={lbl}>Certificate File</label>
        <input type="file" accept=".pdf,.jpg,.png" onChange={(e) => setCertFile(e.target.files[0])} style={{ fontSize: '0.875rem' }} />
      </Modal>

      {/* Bank Account Modal */}
      <Modal isOpen={showBankModal} onClose={() => setShowBankModal(false)} title={editBankId ? 'Edit Bank Account' : 'Add Bank Account'}
        footer={<>
          <Button variant="outlineGray" onClick={() => setShowBankModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={async () => {
            if (!bankForm.bank_name) { alert('Bank name is required'); return; }
            if (editBankId) { await hrms.updateBankAccount(id, editBankId, bankForm); }
            else { await hrms.addBankAccount(id, bankForm); }
            setShowBankModal(false); loadBankAccounts();
          }}>Save</Button>
        </>}>
        {[['bank_name','Bank Name *','text'],['bank_branch','Branch','text'],['ifsc_code','IFSC Code','text'],['account_number','Account Number','text'],['account_holder','Account Holder Name','text']].map(([k,l,t]) => (
          <div key={k} style={{ marginBottom: '0.75rem' }}>
            <label style={lbl}>{l}</label>
            <input style={inp} type={t} value={bankForm[k] || ''} onChange={e => setBankForm(p => ({ ...p, [k]: e.target.value }))} />
          </div>
        ))}
        <label style={lbl}>Account Type</label>
        <select style={{ ...inp, marginBottom: '0.75rem' }} value={bankForm.account_type} onChange={e => setBankForm(p => ({ ...p, account_type: e.target.value }))}>
          <option value="savings">Savings</option>
          <option value="current">Current</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={bankForm.is_primary} onChange={e => setBankForm(p => ({ ...p, is_primary: e.target.checked }))} />
          Set as Primary Account
        </label>
      </Modal>

      {/* Nominee Modal */}
      <Modal isOpen={showNomineeModal} onClose={() => setShowNomineeModal(false)} title={editNomineeId ? 'Edit Nominee' : 'Add Nominee'}
        footer={<>
          <Button variant="outlineGray" onClick={() => setShowNomineeModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={async () => {
            if (!nomineeForm.name) { alert('Name is required'); return; }
            if (!nomineeForm.percentage) { alert('Percentage is required'); return; }
            if (editNomineeId) { await hrms.updateNominee(id, editNomineeId, nomineeForm); }
            else { await hrms.addNominee(id, nomineeForm); }
            setShowNomineeModal(false); loadNominees();
          }}>Save</Button>
        </>}>
        <div style={{ marginBottom: '0.75rem' }}>
          <label style={lbl}>Name *</label>
          <input style={inp} value={nomineeForm.name} onChange={e => setNomineeForm(p => ({ ...p, name: e.target.value.toUpperCase() }))} />
        </div>
        <div style={{ marginBottom: '0.75rem' }}>
          <label style={lbl}>Relationship *</label>
          <select style={inp} value={nomineeForm.relation} onChange={e => setNomineeForm(p => ({ ...p, relation: e.target.value }))}>
            <option value="">Select…</option>
            {RELATIONSHIP_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <div><label style={lbl}>Date of Birth</label><input style={inp} type="date" value={nomineeForm.dob} onChange={e => setNomineeForm(p => ({ ...p, dob: e.target.value }))} /></div>
          <div><label style={lbl}>Aadhar Number</label><input style={inp} value={nomineeForm.aadhar} onChange={e => setNomineeForm(p => ({ ...p, aadhar: e.target.value }))} /></div>
          <div><label style={lbl}>Contact</label><input style={inp} value={nomineeForm.contact} onChange={e => setNomineeForm(p => ({ ...p, contact: e.target.value }))} /></div>
          <div>
            <label style={lbl}>Percentage * (remaining: {100 - nominees.filter(n => n.id !== editNomineeId).reduce((s, n) => s + n.percentage, 0)}%)</label>
            <input style={inp} type="number" min="1" max="100" value={nomineeForm.percentage} onChange={e => setNomineeForm(p => ({ ...p, percentage: e.target.value }))} />
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Documents Tab ─────────────────────────────────────────────────────────────
const DOC_TYPES = [
  { key: 'resume',   label: 'Resume',              icon: '📄', required: true },
  { key: 'aadhaar',  label: 'Aadhaar Copy',         icon: '🪪', required: true },
  { key: 'pan',      label: 'PAN Copy',             icon: '💳', required: true },
  { key: 'passbook', label: 'Bank Passbook (Front)', icon: '🏦', required: true },
];

function DocumentsTab({ empId, docs, onRefresh }) {
  const [uploading, setUploading] = useState('');

  const handleUpload = async (docType, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { alert('Only PDF files are allowed'); return; }
    setUploading(docType);
    const fd = new FormData();
    fd.append('doc_type', docType);
    fd.append('file', file);
    try {
      await hrms.uploadEmpDocument(empId, fd);
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(''); }
  };

  const handleDelete = async (did) => {
    if (!window.confirm('Delete this document?')) return;
    await hrms.deleteEmpDocument(empId, did);
    onRefresh();
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
      {DOC_TYPES.map(dt => {
        const existing = docs.find(d => d.doc_type === dt.key);
        return (
          <div key={dt.key} style={{ background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: '0.75rem', padding: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '1.5rem' }}>{dt.icon}</span>
              <div>
                <p style={{ margin: 0, fontWeight: 600, fontSize: '0.875rem', color: theme.text }}>{dt.label}</p>
                <p style={{ margin: 0, fontSize: '0.72rem', color: dt.required ? theme.danger : theme.textMuted }}>{dt.required ? 'Required · PDF only' : 'Optional · PDF only'}</p>
              </div>
            </div>
            {existing ? (
              <div style={{ background: '#D1FAE5', borderRadius: '0.5rem', padding: '0.6rem 0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <p style={{ margin: 0, fontSize: '0.8rem', fontWeight: 600, color: '#065F46' }}>✓ Uploaded</p>
                  <p style={{ margin: 0, fontSize: '0.72rem', color: '#065F46' }}>{existing.filename}</p>
                </div>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <a href={`/uploads/${existing.filepath}`} target="_blank" rel="noreferrer" style={{ fontSize: '0.75rem', color: theme.primary }}>View</a>
                  <button onClick={() => handleDelete(existing.id)} style={{ background: 'none', border: 'none', color: theme.danger, cursor: 'pointer', fontSize: '0.75rem' }}>Delete</button>
                </div>
              </div>
            ) : (
              <div style={{ background: theme.pageBg, borderRadius: '0.5rem', padding: '0.6rem 0.75rem' }}>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', color: theme.textMuted }}>Not uploaded yet</p>
                <label style={{ cursor: 'pointer' }}>
                  <input type="file" accept=".pdf" style={{ display: 'none' }} onChange={e => handleUpload(dt.key, e.target.files[0])} />
                  <span style={{ padding: '0.4rem 0.875rem', background: uploading === dt.key ? theme.border : theme.primary, color: '#fff', borderRadius: '0.4rem', fontSize: '0.8rem', fontWeight: 500 }}>
                    {uploading === dt.key ? 'Uploading…' : '↑ Upload PDF'}
                  </span>
                </label>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default EmployeeDetail;
